import { Fragment, useState, useEffect, useCallback } from "react";
import { FormattedNumber } from "react-intl";
import moment from "moment";
import ICatalog_Item from "@models/ICatalog_Item";
import { useAppContextNonNull } from "@contexts/AppContext";
import { IInProductionStepDetail } from "@models/DTO/IOnGetInProductionBreakdownDTO";
import IOnGetInProductionAllocationDTO, { IWaitingOrderLine } from "@models/DTO/IOnGetInProductionAllocationDTO";
import IBulkAssignSerialsDTO from "@models/DTO/IBulkAssignSerialsDTO";
import InventoryService from "@services/InventoryService";
import SerialService from "@services/SerialService";
import OwButton from "@components/shared/OwButton/OwButton";
import "./InventoryItemInProductionDetailSidebar.scss";

type Props = {
    catalogItem: ICatalog_Item;
    closeFn: () => void;
};

export const InventoryItemInProductionDetailSidebar = (props: Props) => {
    const { catalogItem, closeFn } = props;
    const { userModel, supplierModel } = useAppContextNonNull();

    const [steps, setSteps] = useState<IInProductionStepDetail[]>([]);
    const [totalInProduction, setTotalInProduction] = useState(0);
    const [catalogItemSku, setCatalogItemSku] = useState<string | undefined>();
    const [catalogItemName, setCatalogItemName] = useState<string | undefined>();
    const [isLoading, setIsLoading] = useState(true);
    const [allocation, setAllocation] = useState<IOnGetInProductionAllocationDTO | null>(null);
    const [expandedStepIds, setExpandedStepIds] = useState<Set<number>>(new Set());
    const [selectedSerialIds, setSelectedSerialIds] = useState<Set<number>>(new Set());
    const [selectedOrderItemGuid, setSelectedOrderItemGuid] = useState<string | null>(null);
    const [isAssigning, setIsAssigning] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const fetchData = useCallback(async () => {
        setIsLoading(true);

        try {
            const [breakdownResult, allocationResult] = await Promise.all([
                InventoryService.getInProductionBreakdown(catalogItem.guid, userModel, supplierModel),
                InventoryService.getInProductionAllocation(catalogItem.guid, userModel, supplierModel),
            ]);

            if (breakdownResult.success && breakdownResult.data) {
                setSteps(breakdownResult.data.steps);
                setTotalInProduction(breakdownResult.data.totalInProduction);
                setCatalogItemSku(breakdownResult.data.catalogItemSku);
                setCatalogItemName(breakdownResult.data.catalogItemName);
            }

            if (allocationResult.success && allocationResult.data) {
                setAllocation(allocationResult.data);
                // Preselect the oldest waiting order (server-sorted) so a
                // received unit can be earmarked with a single click.
                setSelectedOrderItemGuid((prev) => {
                    const stillWaiting = allocationResult.data!.waitingOrders.some((line) => line.orderItemGuid === prev);
                    return stillWaiting ? prev : (allocationResult.data!.waitingOrders[0]?.orderItemGuid ?? null);
                });
            }
        } catch (error) {
            console.error("Error fetching in-production details:", error);
        } finally {
            setIsLoading(false);
        }
    }, [catalogItem.guid, userModel, supplierModel]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const toggleStepExpanded = (stepId: number) => {
        setExpandedStepIds((prev) => {
            const next = new Set(prev);
            if (next.has(stepId)) {
                next.delete(stepId);
            } else {
                next.add(stepId);
            }
            return next;
        });
    };

    const toggleSerialSelected = (serialId: number) => {
        setSelectedSerialIds((prev) => {
            const next = new Set(prev);
            if (next.has(serialId)) {
                next.delete(serialId);
            } else {
                next.add(serialId);
            }
            return next;
        });
    };

    const selectedLine: IWaitingOrderLine | undefined = allocation?.waitingOrders.find(
        (line) => line.orderItemGuid === selectedOrderItemGuid
    );
    const overNeeded = !!selectedLine && selectedSerialIds.size > selectedLine.needed;
    const canAssign = selectedSerialIds.size > 0 && !!selectedOrderItemGuid && !overNeeded && !isAssigning;

    const handleAssign = async () => {
        if (!canAssign || !selectedOrderItemGuid) return;

        try {
            setIsAssigning(true);
            setErrorMessage("");

            const dto: IBulkAssignSerialsDTO = {
                orderItemGuid: selectedOrderItemGuid,
                serialIds: Array.from(selectedSerialIds),
            };

            await SerialService.bulkAssignSerials(dto, userModel, supplierModel);
            setSelectedSerialIds(new Set());
            await fetchData();
        } catch (error: any) {
            console.error("Error assigning serials:", error);
            setErrorMessage(error.message || "Error assigning serials");
        } finally {
            setIsAssigning(false);
        }
    };

    const renderStepRow = (step: IInProductionStepDetail) => {
        const stepSerials = (allocation?.serials || []).filter((serial) => serial.stepId === step.stepId);
        const isExpanded = expandedStepIds.has(step.stepId);
        const earmarkedCount = step.serialCount - stepSerials.length;

        return (
            <Fragment key={step.stepId}>
                <tr className={stepSerials.length ? "expandable-step" : undefined}>
                    <td onClick={stepSerials.length ? () => toggleStepExpanded(step.stepId) : undefined}>
                        {stepSerials.length ? <i className={`fas fa-chevron-${isExpanded ? "down" : "right"} me-2`} /> : null}
                        {step.stepName}
                    </td>
                    <td className="text-end">
                        <FormattedNumber value={step.serialCount} minimumFractionDigits={0} />
                        {earmarkedCount > 0 && <span className="text-muted small ms-1">({earmarkedCount} assigned)</span>}
                    </td>
                </tr>
                {isExpanded &&
                    stepSerials.map((serial) => (
                        <tr key={`serial-${serial.id}`} className="serial-row">
                            <td colSpan={2}>
                                <div className="form-check ms-4">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={`wip-serial-${serial.id}`}
                                        checked={selectedSerialIds.has(serial.id)}
                                        onChange={() => toggleSerialSelected(serial.id)}
                                    />
                                    <label className="form-check-label" htmlFor={`wip-serial-${serial.id}`}>
                                        {serial.value || serial.id}
                                    </label>
                                </div>
                            </td>
                        </tr>
                    ))}
            </Fragment>
        );
    };

    return (
        <div className="InventoryItemInProductionDetailSidebar">
            <div className="sidebar-header">
                <div className="header-content">
                    <h5 className="sidebar-title">In Production Details</h5>
                    <div className="item-info">
                        <strong>{catalogItemSku || catalogItem.sku}</strong>
                        {(catalogItemName || catalogItem.description) && <span> - {catalogItemName || catalogItem.description}</span>}
                    </div>
                    <div className="summary-badge">
                        Total In Production: <FormattedNumber value={totalInProduction} minimumFractionDigits={0} />
                    </div>
                </div>
                <OwButton centerIconClass="fas fa-times" className="btn-danger" fn={closeFn} />
            </div>

            <div className="table-container">
                {isLoading ? (
                    <div className="loading-container">
                        <i className="fas fa-spinner fa-spin fa-2x"></i>
                    </div>
                ) : steps.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-industry fa-3x mb-3"></i>
                        <p>No serials are in production for this item.</p>
                    </div>
                ) : (
                    <table className="steps-table">
                        <thead>
                            <tr>
                                <th>Step</th>
                                <th className="text-end">Serials</th>
                            </tr>
                        </thead>
                        <tbody>{steps.map((step) => renderStepRow(step))}</tbody>
                    </table>
                )}
            </div>

            {!isLoading && steps.length > 0 && (
                <div className="allocation-section">
                    <h6 className="mt-3">Waiting Orders</h6>
                    {!allocation || allocation.waitingOrders.length === 0 ? (
                        <div className="text-muted">No waiting orders for this item.</div>
                    ) : (
                        <>
                            <div className="waiting-orders-box rounded p-1">
                                {allocation.waitingOrders.map((line) => (
                                    <div key={line.orderItemGuid} className="form-check">
                                        <input
                                            type="radio"
                                            className="form-check-input"
                                            name="waitingOrder"
                                            id={`waiting-order-${line.orderItemGuid}`}
                                            checked={selectedOrderItemGuid === line.orderItemGuid}
                                            onChange={() => setSelectedOrderItemGuid(line.orderItemGuid)}
                                        />
                                        <label className="form-check-label" htmlFor={`waiting-order-${line.orderItemGuid}`}>
                                            <strong>{line.orderNo}</strong>
                                            {line.customerName && <span> - {line.customerName}</span>}
                                            <span className="text-muted small ms-1">
                                                ({moment(line.orderDate).format("l")}, {line.orderState})
                                            </span>
                                            <span className="ms-1">Needed: {line.needed}</span>
                                            {line.assignedWip > 0 && (
                                                <span className="text-muted small ms-1">({line.assignedWip} in production assigned)</span>
                                            )}
                                        </label>
                                    </div>
                                ))}
                            </div>
                            {errorMessage && <div className="text-danger small mt-1">{errorMessage}</div>}
                            {overNeeded && (
                                <div className="text-danger small mt-1">Selection exceeds the quantity needed by the selected order.</div>
                            )}
                            <OwButton
                                title="Assign To Order"
                                className="btn-success mt-2"
                                skipTranslate={true}
                                fn={handleAssign}
                                disabled={!canAssign}
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
